import { useCallback, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useSelector } from 'react-redux';

import { GenericFailure, OffsetLimit } from '@playq/services-shared';
import { Filter, Environment, AppID } from '@playq/octopus-common';
import { EnvironmentLadders, LadderSort, LaddersResponse } from '@playq/octopus2-social';
import { LadderTemplate } from '@playq/services-clerk';

import { services2 } from '/api/services2';
import {
  UseQueryOptionsExtended,
  useEitherQuery,
  useQueryPrefetch,
  useMutateQueryDataAfterEntityDeleting,
} from '/api/service-hooks';
import { appToolkit } from '/store';
import { snackbarService } from '/common/snackbarService';
import { QueryHelpers } from '/helpers/query';

import { LeaderboardPromotionPayload } from './types';
import { leaderboardsQueryKeys } from './constants';
import { mutateEnvsAfterLboardDeleting } from './helpers';

const emptyArray: [] = [];
const defaultEnvs = new EnvironmentLadders().serialize();
const emptyFilterBy: {
  [key: string]: Filter;
} = {};

export const useAppsLeaderboardsQuery = (
  iterator: OffsetLimit = QueryHelpers.getMaxIterator(),
  sortBy: LadderSort[] = emptyArray,
  filterBy: {
    [key: string]: Filter;
  } = emptyFilterBy,
  options?: UseQueryOptionsExtended<LaddersResponse | undefined, GenericFailure | Error>
) => {
  const appID = useSelector(appToolkit.selectors.appID);

  const queryClient = useQueryClient();

  const keys = useMemo(
    () => leaderboardsQueryKeys.concat(appID, iterator, sortBy, filterBy),
    [appID, iterator, sortBy, filterBy]
  );

  const isValid = useMemo(() => appID !== undefined, [appID]);
  const enabled = useMemo(() => options?.enabled !== false && isValid, [options?.enabled, isValid]);
  const enablePrefetch = useMemo(
    () => options?.enablePrefetch !== false && isValid,
    [options?.enablePrefetch, isValid]
  );

  const { mutate, refetch, ...res } = useEitherQuery(
    keys,
    () => services2.lboardsService.queryLadders(appID as AppID, iterator, sortBy, filterBy),
    {
      keepPreviousData: true,
      onError: (err) => snackbarService.genericFailure(err),
      ...options,
      enabled,
    }
  );

  const total = useMemo(() => res.data?.total ?? 0, [res.data?.total]);

  const { nextKeys } = useQueryPrefetch({
    keys,
    total,
    enabled: enablePrefetch,
    args: [appID as AppID, iterator, sortBy, filterBy],
    serviceMethod: services2.lboardsService.queryLadders.bind(services2.lboardsService),
  });

  const mutatePromotion = useCallback(
    ({ id, env, demote }: LeaderboardPromotionPayload) => {
      mutate((prevResponse) => {
        if (!prevResponse) {
          return;
        }
        const response = new LaddersResponse();
        response.all = prevResponse.all;
        response.total = prevResponse.total;
        response.iterator = prevResponse.iterator;
        response.envs = prevResponse.envs;

        const envProp = env === Environment.Sandbox ? 'sandbox' : 'live';
        if (demote) {
          response.envs[envProp] = response.envs[envProp].filter(
            (leaderboardID) => leaderboardID.serialize() !== id.serialize()
          );
        } else {
          response.envs[envProp].push(id);
        }
        return response;
      });
    },
    [mutate]
  );

  const mutateDeletedLeaderboard = useMutateQueryDataAfterEntityDeleting({
    queryClient,
    nextPageQueryKey: nextKeys,
    total,
    iterator,
    mutate,
    refetch,
    getID: (l?: LadderTemplate) => l?.id,
    getEntities: (r: LaddersResponse) => r.all,
    setEntities: (r: LaddersResponse, newEntities: LadderTemplate[]) => {
      r.all = newEntities;
    },
    createNewResponse: (prevResponse: LaddersResponse) => new LaddersResponse(prevResponse.serialize()),
    mutateCustomFields: mutateEnvsAfterLboardDeleting,
  });

  return {
    ...res,
    lboards: res.data?.all || emptyArray,
    envs: res.data?.envs.serialize() || defaultEnvs,
    total,
    tags: keys,
    mutatePromotion,
    mutateDeletedLeaderboard,
    refetch,
  };
};
